import type { ModuleDefinition } from "./phase-three-types";

const FIXED_ATTACK_INTERVALS_MS = [
  10,
  20,
  30,
  40,
  50,
  100,
  150,
  200,
  250,
  300,
  350,
  400,
  500,
  600,
  700,
  800,
  900,
  1_000,
  1_100,
  1_200,
  1_300,
  1_400,
  1_500,
  1_600,
  1_700,
  1_800,
  1_900,
  2_000,
  2_500,
  3_000,
  3_500,
] as const;

export function isAllowedAttackIntervalMs(intervalMs: number): boolean {
  if (!Number.isInteger(intervalMs) || intervalMs <= 0) {
    return false;
  }
  if (intervalMs <= 50) {
    return intervalMs % 10 === 0;
  }
  if (intervalMs < 100) {
    return false;
  }
  if (intervalMs < 1_000) {
    return (
      intervalMs % 100 === 0 ||
      intervalMs === 150 ||
      intervalMs === 250 ||
      intervalMs === 350
    );
  }
  if (intervalMs < 2_000) {
    return intervalMs % 100 === 0;
  }
  return (
    intervalMs % 1_000 === 0 ||
    intervalMs === 2_500 ||
    intervalMs === 3_500
  );
}

export function normalizeAttackIntervalMs(intervalMs: number): number {
  if (!Number.isFinite(intervalMs) || intervalMs <= 0) {
    throw new Error("攻击间隔必须是大于0的有限数字。");
  }
  if (isAllowedAttackIntervalMs(intervalMs)) {
    return intervalMs;
  }

  const candidates = new Set<number>(FIXED_ATTACK_INTERVALS_MS);
  const seconds = intervalMs / 1_000;
  for (
    let wholeSecond = Math.max(2, Math.floor(seconds) - 1);
    wholeSecond <= Math.ceil(seconds) + 1;
    wholeSecond += 1
  ) {
    candidates.add(wholeSecond * 1_000);
  }

  return [...candidates].reduce((best, candidate) => {
    const distance = Math.abs(candidate - intervalMs);
    const bestDistance = Math.abs(best - intervalMs);
    if (distance < bestDistance) {
      return candidate;
    }
    if (distance === bestDistance && candidate > best) {
      return candidate;
    }
    return best;
  });
}

export function normalizeModuleAttackCadence(
  module: ModuleDefinition,
): ModuleDefinition {
  let normalized = module;
  if (module.combat) {
    const intervalMs = normalizeAttackIntervalMs(module.combat.intervalMs);
    const damage = normalizeDamageForInterval(
      module.combat.damage,
      module.combat.intervalMs,
      intervalMs,
    );
    normalized = {
      ...normalized,
      combat: {
        ...module.combat,
        damage,
        intervalMs,
      },
    };
  }
  if (
    module.companion?.damage !== undefined &&
    module.companion.intervalMs !== undefined
  ) {
    const intervalMs = normalizeAttackIntervalMs(module.companion.intervalMs);
    const damage = normalizeDamageForInterval(
      module.companion.damage,
      module.companion.intervalMs,
      intervalMs,
    );
    normalized = {
      ...normalized,
      companion: {
        ...module.companion,
        damage,
        intervalMs,
      },
    };
  }
  return normalized;
}

export function formatAttackIntervalSeconds(intervalMs: number): string {
  if (!isAllowedAttackIntervalMs(intervalMs)) {
    throw new Error(`攻击间隔 ${intervalMs}ms 不符合项目精度规则。`);
  }
  return String(intervalMs / 1_000);
}

function normalizeDamageForInterval(
  damage: number,
  previousIntervalMs: number,
  nextIntervalMs: number,
): number {
  if (damage === 0 || previousIntervalMs === nextIntervalMs) {
    return damage;
  }
  return Math.max(
    1,
    Math.round((damage * nextIntervalMs) / previousIntervalMs),
  );
}
